import clsx from 'clsx';
import React from 'react';

import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ContentType, click } from '@tracking';
import { articlePath } from '@utils/queryStringHelper';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import { Article } from '../../interfaces/homepage';
import Byline from '../Byline';
import IconProTagThumbnail from '../Images/Rebranding/IconProTagThumbnail';
import ArticleAsset from './ArticleAsset';
import Highlight from './Highlight';

interface FeaturedArticleProps {
  className?: string;
  featuredArticle: Article;
  isFluid?: boolean;
}

const FeaturedArticle = ({ className, featuredArticle, isFluid = false }: FeaturedArticleProps) => {
  const { env } = useAppContext();
  const {
    authors,
    commentsCount,
    freeBlurb,
    highlight,
    id,
    isPro,
    picture,
    publishedAt,
    slug,
    title,
    newsletterSlug,
  } = featuredArticle;
  const { trackEvent, Track } = useEventTracking({
    contentType: ContentType.Article,
    contentId: id,
    creatorIds: authors?.map((author) => author.id) || [],
  });

  return (
    <Track>
      <article
        className={clsx(
          'flex-1 border-0 border-b border-solid border-grayscale-gray px-16 pb-32 sm:px-0',
          className
        )}
      >
        <a
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.FeaturedArticleImage }))
          }
          href={articlePath({ slug, newsletterSlug })}
          className={clsx('relative -mx-16 mb-16 block sm:mx-0 sm:mb-24', {
            'h-[calc((100vw)*9/16)] sm:h-[calc((100vw-3.5rem)*9/16)] md:h-[calc(712px*9/16)] md:min-w-[712px] lg:h-[calc(884px*9/16)] lg:min-w-[884px]':
              !isFluid,
            'aspect-[16/9] flex-shrink flex-grow text-[1px] leading-none sm:w-full lg:max-w-[884px]':
              isFluid,
          })}
        >
          {isPro && <IconProTagThumbnail className="absolute right-0 top-0" />}
          <ArticleAsset title={title} id={id} picture={picture} env={env} isFeaturedArticle />
        </a>
        {highlight && <Highlight highlight={highlight} />}
        <a
          onClickCapture={() =>
            trackEvent(
              click({ clickTarget: ClickTarget.FeaturedArticleHeadline, clickText: title })
            )
          }
          href={articlePath({ slug, newsletterSlug })}
          className="text-balance"
        >
          <h2 className="m-0 text-balance font-suisse-works text-headline-xs text-primary-black hover:text-primary-magenta sm:text-headline-md lg:text-headline-lg dark:hover:text-primary-pink">
            {title}
          </h2>
          <p className="my-8 line-clamp-5 text-balance font-suisse-neue text-body-md text-primary-black sm:text-body-lg">
            {AuthoredContentReactToHtmlParser(freeBlurb)}
          </p>
        </a>
        <div>
          <Byline
            authors={authors}
            bookmarkContentId={id}
            bookmarkContentType={ContentType.Article}
            newsletterSlug={newsletterSlug}
            commentsCount={commentsCount}
            publishedAt={publishedAt}
            slug={slug}
          />
        </div>
      </article>
    </Track>
  );
};

export default FeaturedArticle;
